/* Copyright (c) 2020-present CloudIO Inc */

import { Item, Page } from 'componentregistry';
import { Platform } from 'componentregistry/platform/Platform';
import { autoPopulateChildren } from 'designer/state';
import { camelCase } from 'lodash-es';
import Store from 'store/Store';
import { GeneratePage } from './ds_registry';

function getPage(ds: string): Page {
  const alias = ds ? camelCase(`${ds}Alias`) : '';
  const page: {
    form: Item<'Form'>;
    root: Item<'Page'>;
    button: Item<'Button'>;
    canvasGrid: Item<'CanvasGrid'>;
    flexItem: Item<'FlexItem'>;
    boxParent: Item<'Box'>;
    dataStore: Item<'DataStore'>;
    flexItem1: Item<'FlexItem'>;
    flexPanel: Item<'FlexPanel'>;
    rowAction: Item<'RowAction'>;
    actionSave: Item<'ActionSave'>;
    iconButton: Item<'IconButton'>;
    rowAction1: Item<'RowAction'>;
    actionReset: Item<'ActionReset'>;
    box: Item<'Box'>;
    iconButton1: Item<'IconButton'>;
    smartSearch: Item<'SmartSearch'>;
    actionCreate: Item<'ActionCreate'>;
    box1: Item<'Box'>;
    triggerOnClick: Item<'TriggerOnClick'>;
    rowNumberColumn: Item<'RowNumberColumn'>;
    triggerOnClick1: Item<'TriggerOnClick'>;
    triggerOnClick2: Item<'TriggerOnClick'>;
    triggerOnClick3: Item<'TriggerOnClick'>;
    triggerOnClick4: Item<'TriggerOnClick'>;
    dataStoreReference: Item<'DataStoreReference'>;
    rowSelectionColumn: Item<'RowSelectionColumn'>;
    verticalSplitPanel: Item<'VerticalSplitPanel'>;
    actionDeleteCurrentRecord: Item<'ActionDeleteCurrentRecord'>;
    actionDeleteSelectedRecords: Item<'ActionDeleteSelectedRecords'>;
  } = {
    actionCreate: {
      children: [],
      itemId: 'actionCreate',
      parent: 'triggerOnClick',
      props: {
        alias,
      },
      type: 'ActionCreate',
    },
    actionDeleteCurrentRecord: {
      children: [],
      itemId: 'actionDeleteCurrentRecord',
      parent: 'triggerOnClick3',
      props: {
        alias,
        confirmationMessage: {
          type: 'text',
          value: 'Are you sure?',
        },
      },
      type: 'ActionDeleteCurrentRecord',
    },
    actionDeleteSelectedRecords: {
      children: [],
      itemId: 'actionDeleteSelectedRecords',
      parent: 'triggerOnClick4',
      props: {
        alias,
        confirmationMessage: {
          type: 'text',
          value: 'Confirm Delete?',
        },
      },
      type: 'ActionDeleteSelectedRecords',
    },
    actionReset: {
      children: [],
      itemId: 'actionReset',
      parent: 'triggerOnClick1',
      props: {
        alias,
      },
      type: 'ActionReset',
    },
    actionSave: {
      children: [],
      itemId: 'actionSave',
      parent: 'triggerOnClick2',
      props: {
        alias,
      },
      type: 'ActionSave',
    },
    button: {
      children: ['triggerOnClick2'],
      itemId: 'button',
      parent: 'box1',
      props: {
        startIcon: {
          iconName: 'save',
          iconType: 'light',
        },
        text: {
          type: 'text',
          value: 'Save',
        },
      },
      type: 'Button',
    },
    box: {
      children: ['smartSearch'],
      itemId: 'box',
      parent: 'boxParent',
      props: {
        flexGrow: 1,
      },
      type: 'Box',
    },
    box1: {
      children: ['iconButton', 'iconButton1', 'button'],
      itemId: 'box1',
      parent: 'boxParent',
      props: {
        boxHeight: '1',
        gap: '8px',
      },
      type: 'Box',
    },
    canvasGrid: {
      children: ['rowNumberColumn', 'rowSelectionColumn'],
      itemId: 'canvasGrid',
      parent: 'verticalSplitPanel',
      props: {
        showBorder: false,
      },
      type: 'CanvasGrid',
    },
    dataStore: {
      children: [],
      itemId: 'dataStore',
      parent: 'root',
      props: {
        alias,
        autoQuery: true,
        dataSource: ds,
        maxRows: 100,
      },
      type: 'DataStore',
    },
    dataStoreReference: {
      children: ['flexPanel'],
      itemId: 'dataStoreReference',
      parent: 'root',
      props: {
        alias,
      },
      type: 'DataStoreReference',
    },
    flexItem: {
      children: ['boxParent'],
      itemId: 'flexItem',
      parent: 'flexPanel',
      props: {
        backgroundColor: 'transparent',
        padding: {
          bottom: 16,
          left: 0,
          right: 0,
          top: 0,
        },
        sizeType: 'auto',
      },
      type: 'FlexItem',
    },
    flexItem1: {
      children: ['verticalSplitPanel'],
      itemId: 'flexItem1',
      parent: 'flexPanel',
      props: {},
      type: 'FlexItem',
    },
    flexPanel: {
      children: ['flexItem', 'flexItem1'],
      itemId: 'flexPanel',
      parent: 'dataStoreReference',
      props: {},
      type: 'FlexPanel',
    },
    form: {
      children: [],
      itemId: 'form',
      parent: 'verticalSplitPanel',
      props: {
        labelAlignment: 'right',
        labelWidth: 160,
      },
      type: 'Form',
    },
    iconButton: {
      children: ['triggerOnClick'],
      itemId: 'iconButton',
      parent: 'box1',
      props: {
        icon: {
          iconName: 'plus',
          iconType: 'light',
        },
        toolTip: {
          type: 'text',
          value: 'Add New',
        },
      },
      type: 'IconButton',
    },
    iconButton1: {
      children: ['triggerOnClick1'],
      itemId: 'iconButton1',
      parent: 'box1',
      props: {
        icon: {
          iconName: 'undo',
          iconType: 'light',
        },
        toolTip: {
          type: 'text',
          value: 'Reset',
        },
      },
      type: 'IconButton',
    },
    root: {
      children: ['dataStore', 'dataStoreReference'],
      itemId: 'root',
      parent: 'root',
      props: { windowTitle: { type: 'text', value: 'Change Me!' } },
      type: 'Page',
    },
    rowAction: {
      children: ['triggerOnClick3'],
      itemId: 'rowAction',
      parent: 'rowSelectionColumn',
      props: {
        icon: {
          iconName: 'trash',
          iconType: 'light',
        },
        iconColor: 'error',
        maxRowsSelected: 1,
        text: {
          type: 'text',
          value: 'Delete Row',
        },
      },
      type: 'RowAction',
    },
    rowAction1: {
      children: ['triggerOnClick4'],
      itemId: 'rowAction1',
      parent: 'rowSelectionColumn',
      props: {
        icon: {
          iconName: 'trash-alt',
          iconType: 'light',
        },
        iconColor: 'error',
        text: {
          type: 'text',
          value: 'Delete Selected Rows',
        },
      },
      type: 'RowAction',
    },
    rowNumberColumn: {
      children: [],
      itemId: 'rowNumberColumn',
      parent: 'canvasGrid',
      props: {
        label: { type: 'text', value: '#' },
        width: 41,
      },
      type: 'RowNumberColumn',
    },
    boxParent: {
      children: ['box', 'box1'],
      itemId: 'boxParent',
      parent: 'flexItem',
      props: {
        boxHeight: '1',
        gap: '8px',
      },
      type: 'Box',
    },
    rowSelectionColumn: {
      children: ['rowAction', 'rowAction1'],
      itemId: 'rowSelectionColumn',
      parent: 'canvasGrid',
      props: {},
      type: 'RowSelectionColumn',
    },
    smartSearch: {
      children: [],
      itemId: 'smartSearch',
      parent: 'box',
      props: {
        gridId: 'canvasGrid',
      },
      type: 'SmartSearch',
    },
    triggerOnClick: {
      children: ['actionCreate'],
      itemId: 'triggerOnClick',
      parent: 'iconButton',
      props: {},
      type: 'TriggerOnClick',
    },
    triggerOnClick1: {
      children: ['actionReset'],
      itemId: 'triggerOnClick1',
      parent: 'iconButton1',
      props: {},
      type: 'TriggerOnClick',
    },
    triggerOnClick2: {
      children: ['actionSave'],
      itemId: 'triggerOnClick2',
      parent: 'button',
      props: {},
      type: 'TriggerOnClick',
    },
    triggerOnClick3: {
      children: ['actionDeleteCurrentRecord'],
      itemId: 'triggerOnClick3',
      parent: 'rowAction',
      props: {},
      type: 'TriggerOnClick',
    },
    triggerOnClick4: {
      children: ['actionDeleteSelectedRecords'],
      itemId: 'triggerOnClick4',
      parent: 'rowAction1',
      props: {},
      type: 'TriggerOnClick',
    },
    verticalSplitPanel: {
      children: ['canvasGrid', 'form'],
      itemId: 'verticalSplitPanel',
      parent: 'flexItem1',
      props: {
        fixedSide: 'end',
        fixedWidth: 400,
        fixedWidthMax: 1800,
        fixedWidthMin: 0,
      },
      type: 'VerticalSplitPanel',
    },
  };
  return page;
}

const GridWithSearchAndEditForm: GeneratePage = async ({
  platform,
  store,
  appUid,
  pageId,
}: {
  platform: Platform;
  store?: Store<unknown> | null;
  appUid: string;
  pageId: string;
}): Promise<Page> => {
  let page = getPage(store ? store.ds : '');
  if (store) {
    page = await autoPopulateChildren({
      platform,
      page,
      store,
      itemId: 'form',
      silent: true,
      appUid,
      pageId,
    });
    page = await autoPopulateChildren({
      platform,
      page,
      store,
      itemId: 'smartSearch',
      silent: true,
      appUid,
      pageId,
    });
    page = await autoPopulateChildren({
      platform,
      page,
      store,
      itemId: 'canvasGrid',
      silent: true,
      appUid,
      pageId,
    });
  }
  return page;
};

export default GridWithSearchAndEditForm;
